import classNames from 'classnames';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TOOLBAR_BUTTON, TOOLBAR_GROUPS } from '@common/enums';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import { DrawingShapeContext } from '@ee-context';
import { ShapeTabContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectDrawingShapesUndoStackIndex, selectHighestLevelUndoStackIndex } from '@redux/slices/drawingShapeSlice';
import { updateSelectedToolbarButton } from '@redux/slices/toolbarCanvasSlice';

import useToolBar from './useToolbar';
import DuplicateIcon from '/public/assets/icons/Copy button.svg';
import RedoIcon from '/public/assets/icons/Redo.svg';
import RotateClockwiseIcon from '/public/assets/icons/Rotate Clockwise.svg';
import RotateCounterClockwiseIcon from '/public/assets/icons/Rotate Counter Clockwise.svg';
import UndoIcon from '/public/assets/icons/Undo.svg';
import ZoomInIcon from '/public/assets/icons/Zoom In.svg';
import ZoomOutIcon from '/public/assets/icons/Zoom Out.svg';
import ZoomToFitIcon from '/public/assets/icons/Zoom to Fit.svg';
import RemoveIcon from '/public/assets/icons/delete.svg';
import TrashIcon from '/public/assets/icons/trash.svg';

import colors from '../drawing-tabs/colors.module.scss';
import './toolbar.scss';

interface ToolType {
    name: TOOLBAR_BUTTON;
    icon: JSX.Element;
    selected: boolean;
    disabled: boolean;
    toolbarGroup: string;
}

const toolbarList: ToolType[] = [
    {
        name: TOOLBAR_BUTTON.UNDO,
        icon: <UndoIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.MAIN_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.REDO,
        icon: <RedoIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: true,
        toolbarGroup: TOOLBAR_GROUPS.MAIN_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.ROTATE_CLOCKWISE,
        icon: <RotateClockwiseIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.MAIN_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.ROTATE_COUNTERCLOCKWISE,
        icon: <RotateCounterClockwiseIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.MAIN_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.DUPLICATE,
        icon: <DuplicateIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.MAIN_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.REMOVE,
        icon: <RemoveIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.MAIN_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.ZOOM_IN,
        icon: <ZoomInIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.ZOOM_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.ZOOM_OUT,
        icon: <ZoomOutIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.ZOOM_TOOLBAR,
    },
    {
        name: TOOLBAR_BUTTON.ZOOM_TO_FIT,
        icon: <ZoomToFitIcon stroke={colors.primaryColor} />,
        selected: false,
        disabled: false,
        toolbarGroup: TOOLBAR_GROUPS.ZOOM_TOOLBAR,
    },
];

interface EnabledOrDisabledIconProps {
    item: ToolType;
    isModalVisible: boolean;
}

const EnabledOrDisabledIcon = ({ item }: EnabledOrDisabledIconProps) => {
    const drawingShapesUndoStackIndex = useAppSelector(selectDrawingShapesUndoStackIndex);
    const { activeDrawingShape } = useContext(DrawingShapeContext);
    const highestLevelUndoStackIndex = useAppSelector(selectHighestLevelUndoStackIndex);

    switch (item.name) {
        case TOOLBAR_BUTTON.REMOVE:
            return <RemoveIcon stroke={colors.primaryColor} />;
        case TOOLBAR_BUTTON.REDO:
            if (drawingShapesUndoStackIndex === highestLevelUndoStackIndex + 1) {
                item.disabled = true;
                return <RedoIcon stroke={colors.disabledColor} />;
            }
            item.disabled = false;
            return <RedoIcon stroke={colors.primaryColor} />;
        case TOOLBAR_BUTTON.DUPLICATE:
            if (!activeDrawingShape) {
                item.disabled = true;
                return <DuplicateIcon stroke={colors.disabledColor} />;
            }
            item.disabled = false;
            return <DuplicateIcon stroke={colors.primaryColor} />;
        default:
            item.disabled = false;
            return item.icon;
    }
};

interface ToolbarItemProps {
    item: ToolType;
    onClick: (tool: ToolType) => void;
    isModalVisible: boolean;
    isToolbarBtnEnabled: boolean;
}

const ToolbarItem = ({ item, onClick, isModalVisible, isToolbarBtnEnabled }: ToolbarItemProps): JSX.Element => {
    const drawingShapesUndoStackIndex = useAppSelector(selectDrawingShapesUndoStackIndex);
    const _isToolbarBtnEnabled =
        isToolbarBtnEnabled || (item.name === TOOLBAR_BUTTON.UNDO && drawingShapesUndoStackIndex > 0);
    return (
        <div
            className={classNames('canvas-toolbar-item', { 'disabled-btn': !_isToolbarBtnEnabled || item.disabled })}
            onClick={() => _isToolbarBtnEnabled && onClick(item)}
            key={item.name}
        >
            <EnabledOrDisabledIcon item={item} isModalVisible={isModalVisible} />
        </div>
    );
};

export const CanvasToolbar = (): JSX.Element => {
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const { removeShape, undoOneAction } = useToolBar();

    const { activeDrawingShape, activeDrawingApplianceName } = useContext(DrawingShapeContext);
    const { collapsed } = useContext(ShapeTabContext);

    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    const isToolbarBtnEnabled = !!activeDrawingShape || !!activeDrawingApplianceName;

    const dialogTitle: string = useMemo(() => {
        return t('ADDRESS_INFORMATION.DELETE_SHAPE', {
            selectedDrawingObjectName: activeDrawingShape?.label ?? activeDrawingApplianceName,
        });
    }, [activeDrawingApplianceName, activeDrawingShape?.label, t]);

    const onConfirmFromConfirmationModal = () => {
        dispatch(updateSelectedToolbarButton(TOOLBAR_BUTTON.REMOVE));
        removeShape();
        setIsModalVisible(false);
    };

    const onCancelFromConfirmationModal = () => {
        setIsModalVisible(false);
    };

    const renderToolbar = (toolbarType: string) => (toolbarList: ToolType[]) =>
        toolbarList
            .filter((tool) => tool.toolbarGroup === toolbarType)
            .map((tool, index) => (
                <ToolbarItem
                    key={index}
                    isModalVisible={isModalVisible}
                    isToolbarBtnEnabled={isToolbarBtnEnabled}
                    item={tool}
                    onClick={() => {
                        if (tool.name === TOOLBAR_BUTTON.REMOVE) {
                            setIsModalVisible(true);
                        } else if (tool.name === TOOLBAR_BUTTON.UNDO) {
                            undoOneAction();
                        } else {
                            dispatch(updateSelectedToolbarButton(tool.name));
                        }
                    }}
                />
            ));

    const renderMainToolbar = renderToolbar(TOOLBAR_GROUPS.MAIN_TOOLBAR);
    const renderZoomToolbar = renderToolbar(TOOLBAR_GROUPS.ZOOM_TOOLBAR);

    return (
        <>
            <div className={`${collapsed ? 'full-width' : 'calculated-width'} canvas-toolbar-area`}>
                {renderMainToolbar(toolbarList)}
            </div>
            <div className={`${collapsed ? 'full-width' : 'calculated-width'} canvas-vertical-toolbar-area`}>
                {renderZoomToolbar(toolbarList)}
            </div>

            <ConfirmationDialog
                title={dialogTitle}
                icon={<TrashIcon />}
                visible={isModalVisible}
                content={t('SCROLLABLE_AREA.COUNTERTOP.REMOVE_COUNTERTOP_DESCRIPTION')}
                onCancel={onCancelFromConfirmationModal}
                onConfirm={onConfirmFromConfirmationModal}
            />
        </>
    );
};
